/**
 * Conversão entre `AlteracaoSimulada` (modelo local do fold) e os payloads das
 * instruções da API de Simulação.
 *
 * O `payload` leva só o DTO do use case. A alteração do fold volta em `anterior`.
 */

import type { InstrucaoPayload, ItemSimulacaoApi } from "../simulacao.gateway";
import { isRecord } from "@/core/utils";
import { parearEquipe, soPeriodoDaEquipe } from "./simulacao.model";
import type { AlteracaoEquipe, AlteracaoSimulada, CenarioSimulacao } from "./simulacao.model";

// ---------------------------------------------------------------------------
// AlteracaoSimulada → InstrucaoPayload  (para os endpoints reais com `simular: true`)
// ---------------------------------------------------------------------------

function payloadBase(
  alteracao: AlteracaoSimulada,
): Pick<InstrucaoPayload, "tipo" | "obraId" | "obraCodigo" | "obraNome"> {
  switch (alteracao.acao) {
    case "definir-equipe":
      return {
        tipo: "equipe",
        obraId: alteracao.obra.id,
        obraCodigo: alteracao.obra.codigo,
        obraNome: alteracao.obra.nome,
      };
    case "remanejar-profissional":
      return {
        tipo: "equipe",
        obraId: alteracao.obra.id,
        obraCodigo: alteracao.obra.codigo,
        obraNome: alteracao.obra.nome,
      };
    case "remanejar-fornecedor":
      return {
        tipo: "fornecedor",
        obraId: alteracao.obra.id,
        obraCodigo: alteracao.obra.codigo,
        obraNome: alteracao.obra.nome,
      };
    case "remanejar-local":
      return {
        tipo: "local",
        obraId: alteracao.obra.id,
        obraCodigo: alteracao.obra.codigo,
        obraNome: alteracao.obra.nome,
      };
    case "ignorar-conflito-profissional":
      return {
        tipo: "equipe",
        obraId: alteracao.obras[0]?.id ?? "",
        obraCodigo: alteracao.obras[0]?.codigo ?? "",
        obraNome: alteracao.obras[0]?.nome ?? "",
      };
    case "ignorar-conflito-fornecedor":
      return {
        tipo: "fornecedor",
        obraId: alteracao.obras[0]?.id ?? "",
        obraCodigo: alteracao.obras[0]?.codigo ?? "",
        obraNome: alteracao.obras[0]?.nome ?? "",
      };
    case "ignorar-conflito-local":
      return {
        tipo: "local",
        obraId: alteracao.obras[0]?.id ?? "",
        obraCodigo: alteracao.obras[0]?.codigo ?? "",
        obraNome: alteracao.obras[0]?.nome ?? "",
      };
  }
}

function payloadContrato(alteracao: AlteracaoSimulada): Record<string, unknown> {
  switch (alteracao.acao) {
    case "definir-equipe":
      return {
        alocacoes: alteracao.novo.map(({ papelObraId, usuarioId, inicio, fim }) => ({
          papelObraId,
          usuarioId,
          inicio,
          fim,
        })),
      };
    case "remanejar-profissional":
      return {
        alocacaoId: alteracao.alocacaoId,
        profissionalSubstitutoId: alteracao.novo.id,
      };
    case "remanejar-fornecedor":
      return {
        alocacaoId: alteracao.alocacaoId,
        fornecedorSubstitutoId: alteracao.novo.id,
      };
    case "remanejar-local":
      return {
        alocacaoId: alteracao.alocacaoId,
        localSubstitutoId: alteracao.novo.id,
      };
    case "ignorar-conflito-profissional":
      return {
        profissionalId: alteracao.profissional.id,
        alocacaoIds: alteracao.alocacaoIds,
      };
    case "ignorar-conflito-fornecedor":
      return {
        fornecedorId: alteracao.fornecedor.id,
        alocacaoIds: alteracao.alocacaoIds,
        conflitoInicio: alteracao.conflito.inicio,
        conflitoFim: alteracao.conflito.fim,
      };
    case "ignorar-conflito-local":
      return {
        localId: alteracao.local.id,
        alocacaoIds: alteracao.alocacaoIds,
        conflitoInicio: alteracao.conflito.inicio,
        conflitoFim: alteracao.conflito.fim,
      };
  }
}

function periodosDaEquipe(alteracao: AlteracaoEquipe) {
  return parearEquipe(alteracao.anterior, alteracao.novo).pares.flatMap(({ membro, previa }) => {
    if (!membro.alocacaoId) return [];
    if (previa && previa.inicio === membro.inicio && previa.fim === membro.fim) return [];
    return [{ alocacaoId: membro.alocacaoId, inicio: membro.inicio, fim: membro.fim }];
  });
}

function operacaoApi(alteracao: AlteracaoSimulada): string {
  if (
    alteracao.acao === "definir-equipe" &&
    soPeriodoDaEquipe(alteracao.anterior, alteracao.novo) &&
    periodosDaEquipe(alteracao).length > 0
  ) {
    return "editar-periodo-equipe";
  }
  return alteracao.acao;
}

function payloadDaOperacao(
  alteracao: AlteracaoSimulada,
  operacao: string,
): Record<string, unknown> {
  if (operacao === "editar-periodo-equipe" && alteracao.acao === "definir-equipe") {
    return { alocacoes: periodosDaEquipe(alteracao) };
  }
  return payloadContrato(alteracao);
}

/** Período de antes da simulação de cada alocação da equipe: o que o core tem gravado. */
function periodosOriginais(alteracao: AlteracaoEquipe) {
  return alteracao.anterior.flatMap((membro) =>
    membro.alocacaoId
      ? [{ alocacaoId: membro.alocacaoId, inicio: membro.inicio, fim: membro.fim }]
      : [],
  );
}

/**
 * Monta a instrução. O `payload` é só o DTO do use case. A alteração completa
 * vai em `anterior`, que o núcleo guarda como JSON livre, para o fold reabrir.
 */
export function alteracaoParaInstrucao(alteracao: AlteracaoSimulada): InstrucaoPayload {
  const base = payloadBase(alteracao);
  const operacao = operacaoApi(alteracao);
  return {
    operacao,
    ...base,
    ...(operacao === "editar-periodo-equipe" && alteracao.acao === "definir-equipe"
      ? { periodosOriginais: periodosOriginais(alteracao) }
      : {}),
    payload: payloadDaOperacao(alteracao, operacao),
    anterior: { ...alteracao },
    conflitosPrevistos: alteracao.conflitosResiduais
      ? Object.entries(alteracao.conflitosResiduais).map(([recurso, count]) => ({
          recurso,
          count,
        }))
      : [],
  };
}

// ---------------------------------------------------------------------------
// ItemSimulacaoApi → AlteracaoSimulada  (para reconstruir o fold)
// ---------------------------------------------------------------------------

function isAlteracaoSimulada(val: unknown): val is AlteracaoSimulada {
  if (!isRecord(val)) return false;
  const acao = val.acao as string | undefined;
  return (
    acao === "definir-equipe" ||
    acao === "remanejar-profissional" ||
    acao === "remanejar-fornecedor" ||
    acao === "remanejar-local" ||
    acao === "ignorar-conflito-profissional" ||
    acao === "ignorar-conflito-fornecedor" ||
    acao === "ignorar-conflito-local"
  );
}

/**
 * Converte um item da API no `AlteracaoSimulada` do fold.
 * A alteração completa volta em `anterior`.
 */
export function instrucaoParaAlteracao(item: ItemSimulacaoApi): AlteracaoSimulada | null {
  if (isAlteracaoSimulada(item.anterior)) return item.anterior;
  return null;
}

/**
 * Converte os itens de uma SimulacaoAtivaApi em `CenarioSimulacao` para uso no fold.
 * Ignora itens cujo `anterior` não é uma alteração conhecida.
 */
export function converterItensCenario(itens: ItemSimulacaoApi[]): CenarioSimulacao {
  const alteracoes = itens
    .map(instrucaoParaAlteracao)
    .filter((a): a is AlteracaoSimulada => a !== null);
  return { alteracoes };
}
